import { useState } from "react";
import { Plus } from "lucide-react";
import { v4 } from "uuid";
import { toast } from "sonner";
import t from "@/lang/lang";
import { Dialog, DialogContent } from "@/shared/components/ui/dialog";
import {
  CommonDialogFooter,
  CommonDialogHeader,
  CommonDialogTrigger,
} from "@/shared/components/dialog/dialog-common";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { InputValidationErrors } from "@/shared/components/errors/input-error-messages";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import { CompanySelect } from "@/services/backend/company/components/company-select";
import { useOmniDataSheetCreateMutation } from "../api/data-sheet-api";

const emptyForm: { label: string; companyId: string; file?: File } = {
  label: "",
  companyId: "",
};

/**
 * DataSheetUploadDialog uploads a PDF as a new data sheet of the service
 * kind. A data sheet's file never changes; a new version is uploaded as a
 * new data sheet.
 */
export function DataSheetUploadDialog({
  serviceKind,
}: {
  serviceKind: string;
}) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [create, { isLoading, error, reset }] =
    useOmniDataSheetCreateMutation();

  const onOpenChange = (next: boolean) => {
    setOpen(next);
    if (!next) {
      setForm(emptyForm);
      reset();
    }
  };

  const onConfirm = () => {
    if (isLoading) {
      return;
    }
    create({ id: v4(), serviceKind, ...form })
      .unwrap()
      .then(() => {
        toast.success(t("Datenblatt hochgeladen"));
        onOpenChange(false);
      });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <CommonDialogTrigger icon={Plus} label={t("Datenblatt")} />
      <DialogContent>
        <CommonDialogHeader
          title={t("Datenblatt hochladen")}
          description={t(
            "Ein Datenblatt ist eine PDF-Datei, die an die Dokumente der Positionen angehängt wird, die darauf verweisen. Die Datei kann nicht ersetzt werden: laden Sie eine neue Version als neues Datenblatt hoch und setzen Sie das alte inaktiv.",
          )}
        />
        <div className="space-y-4">
          <div className="grid content-start gap-1.5">
            <Label>{t("Bezeichnung")}</Label>
            <Input
              value={form.label}
              onChange={(e) => setForm({ ...form, label: e.target.value })}
            />
            <InputValidationErrors error={error} field="label" />
          </div>
          <div className="grid content-start gap-1.5">
            <Label>{t("Unternehmen")}</Label>
            <CompanySelect
              companyId={form.companyId}
              onCompanyIdChange={(companyId) => setForm({ ...form, companyId })}
            />
            <InputValidationErrors error={error} field="companyId" />
          </div>
          <div className="grid content-start gap-1.5">
            <Label>{t("PDF-Datei")}</Label>
            <Input
              type="file"
              accept="application/pdf"
              onChange={(e) =>
                setForm({ ...form, file: e.target.files?.[0] ?? undefined })
              }
            />
            <InputValidationErrors error={error} field="file" />
          </div>
        </div>
        <RTKQueryErrorAlert error={error} />
        <CommonDialogFooter
          isLoading={isLoading}
          onClickAction={onConfirm}
          actionLabel={t("Hochladen")}
        />
      </DialogContent>
    </Dialog>
  );
}
